import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { isSuccess } from "../../../models/response/base-response";
import { useDebounce } from "../../../hooks/use-debounce";
import TransportVehicleService, {
  CrewOption,
  TRANSPORT_VEHICLE_STATUS,
  TRANSPORT_VEHICLE_STATUS_LABEL,
} from "../services/transport-vehicle-service";

export interface VehicleFormValues {
  name: string;
  plate_number: string;
  area: string;
  description: string;
  status: number;
  /** Đội hình — xem user/docs/xe-tai-doi-hinh-ky-thuat.md. null = bỏ trống. */
  driver_id: number | null;
  assistant_id: number | null;
  /** % kg của tài xế; phụ xe = 100 - x. Không có phụ xe → 100. */
  driver_ratio: number;
  /** Chỉ để hiển thị người đang chọn khi họ không nằm trong kết quả tìm kiếm hiện tại. */
  driver_name?: string | null;
  assistant_name?: string | null;
}

export const VehicleFormDefault: VehicleFormValues = {
  name: "",
  plate_number: "",
  area: "",
  description: "",
  status: TRANSPORT_VEHICLE_STATUS.ACTIVE,
  driver_id: null,
  assistant_id: null,
  driver_ratio: 50, // mặc định chia đều 50/50 khi có phụ xe
  driver_name: null,
  assistant_name: null,
};

interface Props {
  onClose: () => void;
  onSubmit: (values: VehicleFormValues) => Promise<boolean>;
  isSubmitting?: boolean;
  titleText?: string;
  submitText?: string;
  initialValues?: Partial<VehicleFormValues>;
  /** Chỉ form SỬA mới cho đổi trạng thái — xe mới luôn tạo ở trạng thái Hoạt động. */
  showStatus?: boolean;
  /** Xe đang sửa — người thuộc CHÍNH xe này không bị coi là "đang ở xe khác". */
  vehicleId?: number;
}

const TransportVehicleFormModal: React.FC<Props> = ({
  onClose,
  onSubmit,
  isSubmitting = false,
  titleText = "Thêm xe vận chuyển",
  submitText = "Tạo mới",
  initialValues = VehicleFormDefault,
  showStatus = false,
  vehicleId,
}) => {
  const [form, setForm] = useState<VehicleFormValues>({ ...VehicleFormDefault, ...initialValues });
  const [errors, setErrors] = useState<{ name?: string; plate_number?: string; crew?: string }>({});

  // Danh sách tài xế toàn hệ thống (BE trả tối đa 50) — tìm theo tên/SĐT, dùng chung cho 2 ô chọn.
  const [crewSearch, setCrewSearch] = useState("");
  const debouncedCrewSearch = useDebounce(crewSearch, 400);
  const [crewOptions, setCrewOptions] = useState<CrewOption[]>([]);
  const { fetch: fetchCrew, loading: crewLoading } = TransportVehicleService.crewOptions();
  const fetchCrewRef = useRef(fetchCrew);
  useEffect(() => {
    fetchCrewRef.current = fetchCrew;
  }, [fetchCrew]);
  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await fetchCrewRef.current(debouncedCrewSearch.trim());
      if (alive && res && isSuccess(res)) setCrewOptions((res.data as CrewOption[]) || []);
    })();
    return () => {
      alive = false;
    };
  }, [debouncedCrewSearch]);

  const handleChange = (field: keyof VehicleFormValues, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "name" || field === "plate_number") {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (field === "driver_id" || field === "assistant_id" || field === "driver_ratio") {
      setErrors((prev) => ({ ...prev, crew: undefined }));
    }
  };

  const pickCrew = (field: "driver_id" | "assistant_id", raw: string) => {
    const id = raw ? Number(raw) : null;
    const opt = crewOptions.find((o) => o.id === id);
    const nameField = field === "driver_id" ? "driver_name" : "assistant_name";
    setForm((prev) => {
      const next = { ...prev, [field]: id, [nameField]: opt?.name ?? null };
      // Bỏ tài xế → không thể còn phụ xe (BE cũng chặn).
      if (field === "driver_id" && !id) {
        next.assistant_id = null;
        next.assistant_name = null;
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, crew: undefined }));
  };

  const hasAssistant = !!form.assistant_id;

  const handleSubmit = async () => {
    const next: { name?: string; plate_number?: string; crew?: string } = {};
    if (!form.name.trim()) next.name = "Vui lòng nhập tên xe";
    if (!form.plate_number.trim()) next.plate_number = "Vui lòng nhập biển số xe";
    if (form.driver_id && form.assistant_id && form.driver_id === form.assistant_id) {
      next.crew = "Tài xế và phụ xe phải là hai người khác nhau";
    } else if (hasAssistant && !(form.driver_ratio > 0 && form.driver_ratio < 100)) {
      next.crew = "Có phụ xe thì tỷ lệ tài xế phải lớn hơn 0 và nhỏ hơn 100";
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const success = await onSubmit({ ...form, driver_ratio: hasAssistant ? form.driver_ratio : 100 });
    if (success) onClose();
  };

  const inputClass = (hasError?: boolean) =>
    `w-full rounded-lg border px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 ${
      hasError
        ? "border-red-400 focus:ring-red-400 focus:border-red-400"
        : "border-slate-300 focus:ring-green-500 focus:border-green-500"
    }`;

  /**
   * Ô chọn 1 người trong đội hình (react-select, có ô tìm kiếm ngay trong dropdown — gõ tên/SĐT → BE lọc).
   * Người đang thuộc xe khác hiện mờ, không chọn được.
   */
  const renderCrewSelect = (
    field: "driver_id" | "assistant_id",
    label: string,
    otherId: number | null,
    disabled = false
  ) => {
    const value = form[field];
    const currentName = field === "driver_id" ? form.driver_name : form.assistant_name;
    type Opt = { value: number; label: string; isDisabled: boolean };
    const options: Opt[] = crewOptions.map((o) => {
      const inOtherVehicle = !!o.vehicle_id && o.vehicle_id !== vehicleId;
      return {
        value: o.id,
        label: `${o.name}${o.phone ? ` · ${o.phone}` : ""}${inOtherVehicle ? ` (đang ở xe ${o.vehicle_plate})` : ""}`,
        isDisabled: inOtherVehicle || o.id === otherId,
      };
    });
    // Người đang chọn nhưng không nằm trong kết quả tìm kiếm hiện tại → vẫn phải hiện để không mất giá trị.
    const selected: Opt | null = value
      ? options.find((o) => o.value === value) || { value, label: currentName || `NV #${value}`, isDisabled: false }
      : null;
    return (
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
        <Select<Opt, false>
          isClearable
          isDisabled={disabled}
          isLoading={crewLoading}
          placeholder={field === "driver_id" ? "Chọn tài xế… (gõ để tìm)" : "Chọn phụ xe… (gõ để tìm)"}
          options={options}
          value={selected}
          // Lọc do BE làm (key_search) — tắt lọc phía client để không lọc 2 lần theo nhãn.
          filterOption={null}
          onInputChange={(text, meta) => {
            if (meta.action === "input-change") setCrewSearch(text);
          }}
          onMenuClose={() => setCrewSearch("")}
          onChange={(o) => pickCrew(field, o ? String(o.value) : "")}
          noOptionsMessage={() => (crewLoading ? "Đang tải…" : "Không tìm thấy tài xế")}
          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
          menuPosition="fixed"
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            control: (base) => ({ ...base, borderColor: errors.crew ? "#f87171" : base.borderColor }),
          }}
        />
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4 md:px-0">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-slate-200 p-5">
          <h2 className="text-lg font-semibold text-slate-800">{titleText}</h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-green-600 hover:bg-green-50 rounded-full w-8 h-8 flex items-center justify-center transition"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Tên xe <span className="text-red-500">*</span>
              </label>
              <input
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="VD: Xe tải 1.5T Biên Hoà"
                className={inputClass(!!errors.name)}
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Biển số <span className="text-red-500">*</span>
              </label>
              <input
                value={form.plate_number}
                onChange={(e) => handleChange("plate_number", e.target.value)}
                placeholder="VD: 51C-123.45"
                className={inputClass(!!errors.plate_number)}
              />
              {errors.plate_number && (
                <p className="mt-1 text-xs text-red-500">{errors.plate_number}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Khu vực</label>
            <input
              value={form.area}
              onChange={(e) => handleChange("area", e.target.value)}
              placeholder="VD: Biên Hoà - Long Thành"
              className={inputClass()}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Mô tả</label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Ghi chú thêm về xe (tải trọng, lịch chạy…)"
              rows={2}
              className={inputClass()}
            />
          </div>

          {/* Đội hình */}
          <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-800">Đội hình xe</h4>
              <p className="text-xs text-slate-500 mt-0.5">
                Chọn xe cho đơn hoặc bàn giao trung chuyển sẽ phân công cả tài xế và phụ xe. Xe chưa có tài
                xế không dùng được. Mỗi người chỉ thuộc một xe.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderCrewSelect("driver_id", "Tài xế", form.assistant_id)}
              {renderCrewSelect("assistant_id", "Phụ xe", form.driver_id, !form.driver_id)}
            </div>
            {hasAssistant && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Tỷ lệ sản lượng tài xế (%)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={99}
                    step={1}
                    value={form.driver_ratio}
                    onChange={(e) => handleChange("driver_ratio", Number(e.target.value))}
                    className={inputClass(!!errors.crew)}
                  />
                </div>
                <p className="text-sm text-slate-600 pb-2">
                  Phụ xe:{" "}
                  <b>{form.driver_ratio > 0 && form.driver_ratio < 100 ? Math.round((100 - form.driver_ratio) * 100) / 100 : "—"}%</b>
                  <span className="block text-xs text-slate-400">
                    Chỉ chia số ký; tiền công = số ký × đơn giá riêng từng người.
                  </span>
                </p>
              </div>
            )}
            {errors.crew && <p className="text-xs text-red-500">{errors.crew}</p>}
          </div>

          {showStatus && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Trạng thái</label>
              <select
                value={form.status}
                onChange={(e) => handleChange("status", Number(e.target.value))}
                className={inputClass()}
              >
                <option value={TRANSPORT_VEHICLE_STATUS.ACTIVE}>
                  {TRANSPORT_VEHICLE_STATUS_LABEL[TRANSPORT_VEHICLE_STATUS.ACTIVE]}
                </option>
                <option value={TRANSPORT_VEHICLE_STATUS.SUSPENDED}>
                  {TRANSPORT_VEHICLE_STATUS_LABEL[TRANSPORT_VEHICLE_STATUS.SUSPENDED]}
                </option>
              </select>
              <p className="mt-1 text-xs text-slate-400">
                "Đang vận chuyển" là trạng thái hệ thống tự nhận khi xe còn hàng trên chuyến — không chọn tay.
              </p>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition"
          >
            Hủy
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-5 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold shadow-sm hover:bg-green-700 transition disabled:opacity-70"
          >
            {isSubmitting ? "Đang lưu..." : submitText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransportVehicleFormModal;
